import React from 'react';
import { X } from 'lucide-react';

/**
 * Modular Badge / StatusChip component following Stride design system.
 */
const Badge = ({
  children,
  variant = 'neutral',
  appearance = 'soft', // 'soft' | 'solid' | 'outline'
  size = 'sm', // 'xs' | 'sm' | 'md'
  withDot = false,
  onRemove,
  icon,
  className = '',
  ...rest
}) => {
  // Styles mapping
  const styleVariants = {
    soft: {
      primary: 'bg-primary-soft text-primary border border-border-hover',
      success: 'bg-emerald-50 text-emerald-700 border border-emerald-200/70',
      warning: 'bg-amber-50 text-amber-800 border border-amber-200/80',
      danger: 'bg-rose-50 text-rose-700 border border-rose-200/70',
      info: 'bg-sky-50 text-sky-700 border border-sky-200/70',
      neutral: 'bg-gray-100 text-gray-700 border border-gray-200',
      secondary: 'bg-slate-100 text-slate-700 border border-slate-200',
    },
    solid: {
      primary: 'bg-primary text-white border border-primary',
      success: 'bg-emerald-600 text-white border border-emerald-600',
      warning: 'bg-amber-500 text-white border border-amber-500',
      danger: 'bg-rose-600 text-white border border-rose-600',
      info: 'bg-sky-600 text-white border border-sky-600',
      neutral: 'bg-gray-700 text-white border border-gray-700',
      secondary: 'bg-slate-800 text-white border border-slate-800',
    },
    outline: {
      primary: 'bg-transparent text-primary border border-primary',
      success: 'bg-transparent text-emerald-700 border border-emerald-400',
      warning: 'bg-transparent text-amber-700 border border-amber-400',
      danger: 'bg-transparent text-rose-600 border border-rose-400',
      info: 'bg-transparent text-sky-700 border border-sky-400',
      neutral: 'bg-transparent text-gray-700 border border-gray-300',
      secondary: 'bg-transparent text-slate-700 border border-slate-300',
    },
  };

  const dotColors = {
    primary: 'bg-primary',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-rose-500',
    info: 'bg-sky-500',
    neutral: 'bg-gray-400',
    secondary: 'bg-slate-500',
  };

  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-[10px] gap-1 rounded',
    sm: 'px-2.5 py-0.5 text-xs gap-1.5 rounded-full',
    md: 'px-3 py-1 text-sm gap-1.5 rounded-full',
  };

  const currentStyles = styleVariants[appearance]?.[variant] || styleVariants.soft.neutral;
  const currentSize = sizeClasses[size] || sizeClasses.sm;

  return (
    <span
      className={`
        inline-flex items-center font-medium leading-none select-none tracking-normal
        ${currentSize}
        ${currentStyles}
        ${className}
      `}
      {...rest}
    >
      {withDot && (
        <span
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColors[variant] || 'bg-current'}`}
        />
      )}

      {icon && <span className="shrink-0 flex items-center">{icon}</span>}

      <span>{children}</span>

      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
          className="ml-0.5 -mr-1 p-0.5 hover:opacity-75 focus:outline-none"
          title="Remove"
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
};

export default Badge;

